import { Button, Chip } from '@viaja/design-system';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { useChangePassword, useLogout, useMe } from '../../lib/queries';
import { type ThemePref, useTheme } from '../../lib/theme';
import { SubScreen } from '../../ui/AppShell';
import { Sheet } from '../../ui/Sheet';
import { TextField } from '../../ui/TextField';
import { Notice } from '../../ui/feedback';

const THEMES: { id: ThemePref; label: string }[] = [
  { id: 'system', label: 'Do sistema' },
  { id: 'light', label: 'Claro' },
  { id: 'dark', label: 'Escuro' },
];

export function AccountPage() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <SubScreen title="Sua conta">
      <div className="app-head">
        <h1 className="display-title">{me.data?.name}</h1>
        <p className="small app-meta">{me.data?.email}</p>
      </div>

      <section className="app-section" aria-labelledby="tema">
        <h2 id="tema" className="eyebrow vc-eyebrow">
          Aparência
        </h2>
        <div className="app-wrap">
          {THEMES.map((t) => (
            <Chip key={t.id} selected={theme === t.id} onClick={() => setTheme(t.id)}>
              {t.label}
            </Chip>
          ))}
        </div>
      </section>

      <section className="app-section" aria-labelledby="seg">
        <h2 id="seg" className="eyebrow vc-eyebrow">
          Segurança
        </h2>
        <div>
          <Button variant="secondary" size="sm" onClick={() => setPwOpen(true)}>
            Trocar senha
          </Button>
        </div>
      </section>

      <div className="app-actionbar">
        <Button
          variant="secondary"
          block
          disabled={logout.isPending}
          onClick={() =>
            logout.mutate(undefined, { onSettled: () => navigate('/entrar', { replace: true }) })
          }
        >
          Sair
        </Button>
      </div>

      <PasswordSheet open={pwOpen} onClose={() => setPwOpen(false)} />
    </SubScreen>
  );
}

function PasswordSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const change = useChangePassword();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [done, setDone] = useState(false);
  const fields = change.error instanceof ApiError ? change.error.fields : {};

  const close = () => {
    setCurrent('');
    setNew('');
    setDone(false);
    change.reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate({ currentPassword, newPassword }, { onSuccess: () => setDone(true) });
  };

  return (
    <Sheet open={open} title="Trocar senha" onClose={close}>
      {done ? (
        <>
          <p className="body">Senha trocada. Os outros aparelhos vão pedir para entrar de novo.</p>
          <Button block onClick={close}>
            Pronto
          </Button>
        </>
      ) : (
        <form className="app-stack" onSubmit={submit} noValidate>
          <TextField
            label="Senha atual"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            error={fields.currentPassword}
            data-autofocus
          />
          <TextField
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
            error={fields.newPassword}
            hint="Pelo menos 8 caracteres."
          />
          {change.error && Object.keys(fields).length === 0 ? <Notice>{change.error.message}</Notice> : null}
          <Button type="submit" block disabled={change.isPending}>
            Trocar senha
          </Button>
        </form>
      )}
    </Sheet>
  );
}
